import React, { useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  ApiTypesEnum,
  FindCarrierAiInfoOutDTO,
  FindCarrierResponseOutDTO,
  FindCarrierTypeEnum,
  LabelPreview,
} from '@api/ringil-api.v2.tsx';

import SvgChevron from '@icons/chevron-thin.svg?react';
import SvgGroup from '@icons/grid-horizontal-inverted.svg?react';
import SvgUser from '@icons/user-inverted.svg?react';
import SvgAPICarrier from '@icons/microchip.svg?react';
import SvgInfo from '@icons/circle-info.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgClose from '@icons/close.svg?react';
import SvgRobot from '@icons/ai-robot.svg?react';
import SvgSelected from '@icons/check-circle.svg?react';
import { Link } from 'react-router-dom';
import useNavigation from '@hooks/useNavigation.tsx';
import clsx from 'clsx';
import { Carrier } from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import { isSameArray } from '@utils/utils.tsx';
import SvgCarrier from '@icons/truck.svg?react';
import { toTestIdSlug } from '@utils/testUtils';

interface DropdownItem {
  carrier: FindCarrierResponseOutDTO;
  type: FindCarrierTypeEnum;
  label: string;
  memberCount?: number;
  labels?: LabelPreview[];
  groupNames?: string[];
  alertsCount?: number;
  parentLabel?: string;
  aiInfos?: FindCarrierAiInfoOutDTO[];
  apiTypes: ApiTypesEnum[];
}

interface CarrierDropdownProps {
  values?: Carrier[];
  carriers: FindCarrierResponseOutDTO[];
  onSelect?: (values: Carrier[]) => void;
  multiSelect?: boolean;
  selectAllEnabled?: boolean;
  dataTestId?: string;
}

const CarrierDropdown: React.FC<CarrierDropdownProps> = ({
                                                      values = [],
                                                      carriers,
                                                      onSelect,
                                                      multiSelect = true,
                                                      selectAllEnabled = true,
                                                      dataTestId,
                                                    }) => {

  const { tr } = useTranslationLgs();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<DropdownItem[]>([]);
  const [filteredValues, setFilteredValues] = useState<DropdownItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownListRef = useRef<HTMLDivElement>(null);
  const maxCarrierNumber = 16;

  const [options, setOptions] = useState<DropdownItem[]>([]);
  const navigation = useNavigation();

  useEffect(() => {
    const groupCarriers = carriers
        .filter(item => item.findCarrierType === FindCarrierTypeEnum.GROUP)
        .sort((a, b) => a.name.localeCompare(b.name));

    const apiCarriers = carriers
        .filter(item => item.findCarrierType === FindCarrierTypeEnum.API)
        .sort((a, b) => a.name.localeCompare(b.name));

    const plainCarriers = carriers
        .filter(item => item.findCarrierType === FindCarrierTypeEnum.CARRIER)
        .sort((a, b) => a.name.localeCompare(b.name));

    const userCarriers = carriers
        .filter(item => item.findCarrierType === FindCarrierTypeEnum.CARRIER_USER)
        .sort((a, b) => a.name.localeCompare(b.name));

    const orderedData: FindCarrierResponseOutDTO[] = [
      ...groupCarriers,
      ...apiCarriers,
      ...plainCarriers.flatMap(carrier => [
        carrier,
        ...userCarriers.filter(user => user.id === carrier.id),
      ]),
    ];

    const toDropdownItem = (item: FindCarrierResponseOutDTO): DropdownItem => ({
      carrier: item,
      type: item.findCarrierType,
      label: item.name,
      memberCount: (item.carrierIds?.length || 0),
      labels: item.labels,
      alertsCount: item.carrierAdditionalInfos?.length || 0,
      parentLabel: (() => {
        if (item.findCarrierType === FindCarrierTypeEnum.CARRIER_USER) {
          const parent = carriers.find(carrier => carrier.id === item.id && carrier.findCarrierType === FindCarrierTypeEnum.CARRIER);
          return parent?.name;
        }
        return undefined;
      })(),
      aiInfos: item.aiInfos,
      apiTypes: item.apiTypes,
      groupNames: item.groupNames,
    });

    setOptions(orderedData.map(item => toDropdownItem(item)));
  }, [carriers]);

  useEffect(() => {
    if (values && values.length > 0) {
      if (!multiSelect && values.length > 1) {
        setSelectedValues([]);
      } else {
        const selected = options.filter(option =>
          values.some(val =>
            val.id === option.carrier.id &&
            isSameArray(val.userIds, option.carrier.userIds),
          ),
        );

        setSelectedValues(selected);
      }
    } else {
      setSelectedValues([]);
    }
  }, [values, options]);

  const scrollIntoView = (index: number) => {
    const dropdownList = document.querySelector('[data-dropdown-list]');
    const item = document.querySelector(`[data-dropdown-item="${index}"]`);

    if (dropdownList && item) {
      const dropdownRect = dropdownList.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();

      if (itemRect.top < dropdownRect.top) {
        dropdownList.scrollTop -= dropdownRect.top - itemRect.top;
      } else if (itemRect.bottom > dropdownRect.bottom) {
        dropdownList.scrollTop += itemRect.bottom - dropdownRect.bottom;
      }
    }
  };

  useEffect(() => {
    if (focusedIndex >= 0) {
      scrollIntoView(focusedIndex);
    }
  }, [focusedIndex]);

  useEffect(() => {
    const filtered = options
      .filter(item => {
      if (!searchTerm) return true;
      return (
        item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.parentLabel && item.parentLabel.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.labels && item.labels.some(label => label.labelValue.toLowerCase().includes(searchTerm.toLowerCase()))) ||
        (item.groupNames && item.groupNames.some(name => name.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    });

    setFilteredValues(filtered);
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const areArraysEqual = (a?: string[], b?: string[]) =>
    Array.isArray(a) && Array.isArray(b) &&
    a.length === b.length &&
    a.every((val, idx) => val === b[idx]);

  const isSame = (a: DropdownItem, b: DropdownItem) => {
    if (a.type === FindCarrierTypeEnum.CARRIER_USER && b.type === FindCarrierTypeEnum.CARRIER_USER) {
      return a.carrier.id === b.carrier.id && areArraysEqual(a.carrier.userIds, b.carrier.userIds);
    }
    return a.carrier.id === b.carrier.id && a.type === b.type;
  };

  function mapDropdownItemToCarrier(item: DropdownItem): Carrier {
    const existingCarrier = values.find(
      value => value.id === item.carrier.id && isSameArray(value.userIds, item.carrier.userIds),
    );
    return {
      id: item.carrier.id,
      userIds: item.carrier.userIds,
      carrierType: item.type,
      apiTypes: item.apiTypes,
      carrierIds: item.carrier.carrierIds,
      _dachserDeliveryTermCode: existingCarrier?._dachserDeliveryTermCode,
      _dachserProductTypeCode: existingCarrier?._dachserProductTypeCode,
      _schenkerProductTypeCode: existingCarrier?._schenkerProductTypeCode,
      _schenkerAddonsTypeCodes: existingCarrier?._schenkerAddonsTypeCodes,
      _dhlProductTypeCode: existingCarrier?._dhlProductTypeCode,
      _dhlCourierRequested: existingCarrier?._dhlCourierRequested,
      _dhlPickupReadyTime: existingCarrier?._dhlPickupReadyTime,
      _dhlPickupCloseTime: existingCarrier?._dhlPickupCloseTime,
      _dhlPickupCapabilities: existingCarrier?._dhlPickupCapabilities,
      _dhlDifferentPayer: existingCarrier?._dhlDifferentPayer,
      _dhlPayerAccountNumber: existingCarrier?._dhlPayerAccountNumber,
      _dhlApplyToDutiesTaxes: existingCarrier?._dhlApplyToDutiesTaxes,
      _fedexProductTypeCode: existingCarrier?._fedexProductTypeCode,
      _fedexCourierRequested: existingCarrier?._fedexCourierRequested,
      _fedexPickupDate: existingCarrier?._fedexPickupDate,
      _fedexPickupReadyTime: existingCarrier?._fedexPickupReadyTime,
      _fedexPickupCloseTime: existingCarrier?._fedexPickupCloseTime,
      _fedexPayerType: existingCarrier?._fedexPayerType,
      _fedexPayerAccountNumber: existingCarrier?._fedexPayerAccountNumber,
      _fedexPayerCountry: existingCarrier?._fedexPayerCountry,
      _fedexDeclaredValue: existingCarrier?._fedexDeclaredValue,
      _fedexDeclaredValueCurrency: existingCarrier?._fedexDeclaredValueCurrency,
      _fedexSignatureOption: existingCarrier?._fedexSignatureOption,
      _fedexPackagingType: existingCarrier?._fedexPackagingType,
      _fedexBookingConfirmationNumber: existingCarrier?._fedexBookingConfirmationNumber,
      _fedexAccountId: existingCarrier?._fedexAccountId,
      _fedexAccountRequired: existingCarrier?._fedexAccountRequired,
    };
  }

  const handleSelectValue = (items: DropdownItem[]) => {

    if (items.length === 0) {
      setSelectedValues([]);
      if (onSelect) onSelect([]);
      return;
    }

    let newSelectedValues: DropdownItem[];

    if (multiSelect) {
      const allSelected = items.every(item =>
        selectedValues.some(selected => isSame(selected, item))
      );
      if (allSelected) {
        newSelectedValues = selectedValues.filter(
          selected => !items.some(item => isSame(selected, item))
        );
      } else {
        newSelectedValues = [
          ...selectedValues,
          ...items.filter(
            item => !selectedValues.some(selected => isSame(selected, item))
          ),
        ];
      }
    } else {
      newSelectedValues = [items[0]];
      setIsOpen(false);
    }

    setSelectedValues(newSelectedValues);
    if (onSelect) {
      onSelect?.(
        newSelectedValues.map(item => mapDropdownItemToCarrier(item)),
      );
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);

    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleChipRemove = (item: DropdownItem) => {
    const newSelectedValues = selectedValues.filter(
      p => !isSame(p, item),
    );
    setSelectedValues(newSelectedValues);
    if (onSelect) {
      onSelect?.(
        newSelectedValues.map(item => mapDropdownItemToCarrier(item)),
      );
    }
    setSearchTerm('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && searchTerm === '' && selectedValues.length > 0) {
      e.preventDefault();
      const lastItem = selectedValues[selectedValues.length - 1];
      handleChipRemove(lastItem);
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        setFocusedIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev =>
          prev < filteredValues.length - 1 ? prev + 1 : prev,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex === 0) {
          if (filteredValues.every(filtered => selectedValues.some(selected => isSame(filtered, selected)))) {
            handleSelectValue([]);
            break;
          } else {
            handleSelectValue(filteredValues);
            break;
          }
        }
        if (focusedIndex > 0 && focusedIndex < filteredValues.length) {
          handleSelectValue([filteredValues[focusedIndex - 1]]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const getIconByType = (type: FindCarrierTypeEnum) => {
    switch (type) {
      case FindCarrierTypeEnum.CARRIER:
        return <SvgCarrier className="w-4 h-4" />;
      case FindCarrierTypeEnum.CARRIER_USER:
        return <SvgUser className="w-4 h-4" />;
      case FindCarrierTypeEnum.GROUP:
        return <SvgGroup className="w-4 h-4" />;
      case FindCarrierTypeEnum.API:
        return <SvgAPICarrier className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const renderInfoIcon = (item: DropdownItem) => {
    if (item.type === FindCarrierTypeEnum.API || item.type === FindCarrierTypeEnum.CARRIER_USER) {
      return null;
    }

    const urlFunction = item.type === FindCarrierTypeEnum.CARRIER
      ? navigation.urlFunctions.createCarrierDetail
      : navigation.urlFunctions.createCarrierGroupDetail;

    return (
      <Link
        to={navigation.createNavigationLink(urlFunction(item.carrier.id))}
        className="ml-1.5 flex items-center"
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
      >
        <SvgInfo className="w-4 h-4 text-content-brand" />
      </Link>
    );
  };

  const renderAiLabel = (item: DropdownItem) => {

    return (
      <div className="flex flex-row flex-1 items-center gap-1">
        <SvgRobot className="h-4 w-4" />
        {item.aiInfos.map((item, idx, arr) => (
          <span key={item.id}
                className="text-[#5B85DE] text-right font-['DM_Sans'] text-[12px] font-normal leading-normal tracking-[0.24px]">
            {item.info}
            {idx < arr.length - 1 && <span
              className="mx-1 text-content-secondary text-xs font-normal font-['DM_Sans'] leading-normal tracking-[0.24px]">·</span>}
          </span>
        ))}
      </div>
    );
  };

  const renderSubLabel = (item: DropdownItem) => {

    return (
      <div className="flex flex-row flex-1">
        {(item.type === FindCarrierTypeEnum.CARRIER || item.type === FindCarrierTypeEnum.CARRIER_USER) && ((item.labels && item.labels.length > 0) || item.groupNames && item.groupNames.length > 0) ? (
          <div className="flex items-center gap-2">
            {item.labels && item.labels.length > 0 && (
              <div className="flex w-[72px]">
                {item.labels.map((label, idx) => (
                  <div
                    key={idx}
                    className={clsx(
                      'h-2 flex-1',
                      item.labels.length === 1 && 'rounded',
                      idx === 0 && item.labels.length > 1 && 'rounded-l',
                      idx === item.labels.length - 1 && item.labels.length > 1 && 'rounded-r',
                    )}
                    style={{ backgroundColor: label.labelColor }}
                  />
                ))}
              </div>
            )}
            <span
              className="text-content-secondary text-xs font-normal font-['DM_Sans'] leading-normal tracking-[0.24px] overflow-hidden text-ellipsis whitespace-nowrap">
              {[...(item.groupNames ?? [])].join(' · ')}
            </span>
          </div>
        ) : item.type === FindCarrierTypeEnum.GROUP ? (
          tr('CarrierDropdown.numberOfMembers', 'members: {{count}}', { count: item.memberCount })
        ) : (
          ''
        )}
      </div>
    );
  };

  const renderAllItem = (isFocused: boolean = false) => {
    return (
      <div
        className={clsx(
          'flex flex-row py-1.5 px-3.5 items-center gap-4 self-stretch h-11 hover:bg-content-quaternary',
          isFocused
            ? 'bg-content-quaternary'
            : filteredValues.every(filtered =>
              selectedValues.some(selected => isSame(filtered, selected)),
            )
              ? 'bg-content-brandMedium'
              : 'bg-content-negativePrimary',
        )}
      >
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          <div
            className="flex flex-row self-stretch text-content-primary font-['DM_Sans'] text-sm font-normal leading-normal overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {
              filteredValues.every(filtered =>
                selectedValues.some(selected => isSame(filtered, selected)),
              ) ? tr('CarrierDropdown.dropdownItemDeSelectAll', 'Deselect all {{count}}', { count: filteredValues.length })
                : tr('CarrierDropdown.dropdownItemSelectAll', 'Select all {{count}}', { count: filteredValues.length })
            }
          </div>
        </div>
      </div>
    );
  };

  const renderDropdownItem = (item: DropdownItem, isFocused: boolean = false) => {
    return (
      <div
        className={clsx(
          'flex flex-row py-1.5 items-center gap-4 self-stretch hover:bg-content-quaternary',
          item.type === FindCarrierTypeEnum.CARRIER_USER ? 'pl-6 pr-2' : 'px-2',
          isFocused
              ? 'bg-content-quaternary'
            : selectedValues.some(
              selected => isSame(item, selected),
            )
              ? 'bg-content-brandMedium'
              : 'bg-content-negativePrimary',
          (item.aiInfos && item.aiInfos.length > 0 && item.labels && item.labels.length > 0) ? 'h-16' : 'h-11',
        )}
      >
        <div
          className={clsx(
            'flex justify-center items-center rounded-full text-content-secondary bg-content-quaternary border border-solid',
            item.type === FindCarrierTypeEnum.CARRIER_USER ? 'w-[26px] h-[26px]' : 'w-[34px] h-[34px]',
            item.alertsCount > 0 ? 'border-complementary-error' : 'border-content-negativePrimary',
          )}
        >
          {selectedValues.some(selected => isSame(item, selected))
            ? <SvgSelected className={clsx('text-content-brand',
                  item.type === FindCarrierTypeEnum.CARRIER_USER ? 'w-4 h-4' : 'w-6 h-6'
                )}
              />
            : getIconByType(item.type)

          }
        </div>
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          <div
            className="flex flex-row self-stretch text-content-primary font-['DM_Sans'] text-sm font-normal leading-normal overflow-hidden text-ellipsis whitespace-nowrap items-center"
          >
            {item.label}
            {renderInfoIcon(item)}
            {item.type === FindCarrierTypeEnum.CARRIER_USER && (
              <span
                className="text-content-secondary pl-3 font-['DM_Sans'] text-xs font-normal leading-normal">
                {item.parentLabel}
              </span>
            )}
          </div>
          {item.aiInfos && item.aiInfos.length > 0 && (
            <div
              className="flex flex-row"
            >
              {renderAiLabel(item)}
            </div>
          )}
          <div
            className="text-content-secondary font-['DM_Sans'] text-xs font-normal leading-normal tracking-[0.24px] overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {renderSubLabel(item)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div ref={dropdownRef} className="relative w-full" data-testid={dataTestId}>
        <div
          className={`r-input-wrapper flex items-center border`}
        >
          <div
            className="flex gap-2 p-1 items-center justify-between w-full min-h-[38px]"
          >
            <div className="flex flex-wrap flex-1 gap-2 items-center">
              {selectedValues.slice(0, Math.min(selectedValues.length, maxCarrierNumber)).map((item, index) => (
                <div
                  key={index}
                  data-testid={dataTestId ? `${dataTestId}-selected-${toTestIdSlug(item.label)}` : undefined}
                  className="flex h-5 py-1.5 px-1 justify-end items-center gap-3 rounded border border-solid border-content-secondary"
                >
                  <span
                    className="text-content-primary font-['DM_Sans'] text-[12px] font-normal leading-normal tracking-[0.24px]"
                  >
                    {item.type === FindCarrierTypeEnum.CARRIER_USER && item.parentLabel
                      ? `${item.parentLabel} - ${item.label}`
                      : item.label}
                  </span>
                  <div
                    data-testid={dataTestId ? `${dataTestId}-selected-${toTestIdSlug(item.label)}-remove` : undefined}
                    onClick={e => {
                    e.stopPropagation();
                    handleChipRemove(item);
                  }}>
                    <SvgClose className="text-content-brand cursor-pointer transition-all duration-200 hover:text-complementary-brandDark" />
                  </div>
                </div>
              ))}
              {selectedValues.length > maxCarrierNumber && (
                <div
                  className="flex flex-row h-5 py-1.5 px-1 justify-end items-center gap-1 rounded cursor-pointer"
                  onClick={() => setIsOpen(!isOpen)}
                >
                  <SvgPlus className="text-content-brand cursor-pointer w-2.5 h-2.5 transition-all duration-200 hover:text-complementary-brandDark" />
                  <span
                    className="text-content-brand font-['DM_Sans'] text-[12px] font-normal leading-normal tracking-[0.24px]"
                  >
                    {tr('CarrierDropdown.plusCarriers', '{{count}} items', { count: selectedValues.length - maxCarrierNumber })}
                  </span>
                </div>
              )}
              {(multiSelect || selectedValues.length === 0) && (
                <input
                  className="p-no-border min-w-52 flex-1 p-2 font-sans text-[15px] outline-none bg-transparent "
                  type="text"
                  value={searchTerm}
                  onClick={() => setIsOpen(prev => !prev)}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={tr('CarrierDropdown.inputPlaceholder', 'Add carrier or group...')}
                />
              )}
            </div>
            <div className="flex flex-row items-center justify-end">
              <SvgChevron
                className={clsx(
                  'w-[12px] h-[12px] flex-shrink-0 mx-2 transition-transform cursor-pointer',
                  isOpen ? 'rotate-180' : '',
                )}
                onClick={() => setIsOpen(prev => !prev)}
              />
            </div>
          </div>
        </div>

        {isOpen && (
          <div
            ref={dropdownListRef}
            className="absolute top-[calc(100%+4px)] left-0 right-0 max-h-[400px] flex flex-col gap-0.5 overflow-y-auto bg-content-quaternary border border-[var(--base-basesecondary)] border-t-0 rounded-b-[4px] z-10 shadow-[0_4px_8px_rgba(0,0,0,0.1)] mt-[-1px]"
            data-dropdown-list
          >

            {filteredValues.length > 0 ? (
              <>
                {selectAllEnabled && (
                  <div
                    key={0}
                    data-dropdown-item={0}
                    className="cursor-pointer"
                    onClick={() => {
                      filteredValues.every(filtered =>
                        selectedValues.some(selected => isSame(filtered, selected)),
                      ) ? handleSelectValue([])
                        : handleSelectValue(filteredValues);
                    }}
                  >
                    {renderAllItem(0 === focusedIndex)}
                  </div>
                )}
                {filteredValues.map((item, index) => (
                  <div
                    key={`${item.carrier.id}-${item.carrier.userIds?.join(',') ?? ''}-${index}`}
                    data-dropdown-item={index + 1}
                    data-testid={dataTestId ? `${dataTestId}-${toTestIdSlug(item.label)}` : undefined}
                    className="cursor-pointer"
                    onClick={() => {
                      handleSelectValue([item]);
                    }}
                  >
                    {renderDropdownItem(item, index + 1 === focusedIndex)}
                  </div>
                ))}
              </>
            ) : (
              <div className="p-3 text-content-secondary text-center italic">
                {tr('CarrierDropdown.noResults', 'No items found')}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};


export default CarrierDropdown;